import { UAParser } from 'ua-parser-js';
import hash from 'object-hash';

import { createAction } from 'actions/utils';
import {
    CLEAR_ALL,
    LOGIN_INFO_REQUEST,
    LOGIN_REQUEST,
    LOGIN_SUCCESS,
    LOGIN_ERROR,
    LOGOUT_REQUEST,
    LOGOUT_SUCCESS,
    LOGOUT_ERROR,
    SWITCH_IMPL_REQUEST,
    SWITCH_IMPL_SUCCESS,
    SWITCH_IMPL_ERROR,
    GET_USER_DATA_REQUEST,
    GET_USER_DATA_SUCCESS,
    UPDATE_USER_DATA_SUCCESS,
    GET_USER_DATA_ERROR,
    GET_CFM_AUTOLOGIN_REQUEST,
    GET_CFM_AUTOLOGIN_SUCCESS,
    GET_CFM_AUTOLOGIN_ERROR,
    GET_LITERALS_REQUEST,
    GET_LITERALS_SUCCESS,
    OPEN_2FA,
    CLOSE_2FA,
    LOGIN_2FA_SUCCESS,
    GET_LOGIN_INFO_SUCCESS,
    LOGIN_OAUTH_REQUEST,
    LOGIN_OAUTH_SUCCESS,
    RESET_USER_LOGIN_INFO,
    SET_SCOPED_TOKEN,
} from 'constants/ActionTypes';
import { ALLOW_LANG } from 'constants/App';
import { Core } from 'managers';
import { setupMomentLocale } from 'utils/datetime';
import { getLiterals } from './Literals';
import { getPermissions } from './Permissions';

const mapperLangCode = {
    es: 'es-es',
    en: 'en-gb',
    us: 'en-us',
    fr: 'fr-fr',
    de: 'de-de',
    it: 'it-it',
    pt: 'pt-pt',
    br: 'pt-br',
    ru: 'ru-ru',
};

export function getLoggedUserData() {
    return (dispatch) => {
        dispatch(createAction(GET_USER_DATA_REQUEST, {}));
        dispatch(createAction(GET_LITERALS_REQUEST, {}));
        return Core.authenticationManager
            .getLoggedUserData()
            .then((user) => {
                user.lang = user.lang ? user.lang.toLowerCase() : 'en';
                user.lang = ALLOW_LANG.indexOf(user.lang) !== -1 ? user.lang : 'en';
                user.langCode = mapperLangCode[user.lang];
                setupMomentLocale(user.lang);
                dispatch(createAction(GET_USER_DATA_SUCCESS, user));
                return dispatch(getLiterals(user.lang));
            })
            .then(() => {
                return Core.permissionsManager.changeScope(['pictures']);
            })
            .then((scopedToken) => {
                dispatch(createAction(SET_SCOPED_TOKEN, { scopedToken }));
            })
            .catch((error) => {
                dispatch(
                    createAction(GET_USER_DATA_ERROR, {
                        error: true,
                        errorType: error,
                    }),
                );
                throw error;
            });
    };
}

export function updateLoggedUserData() {
    return (dispatch) =>
        Core.authenticationManager
            .getLoggedUserData()
            .then((user) => {
                dispatch(createAction(UPDATE_USER_DATA_SUCCESS, user));
            })
            .catch(console.error);
}

export function logout() {
    return (dispatch) => {
        dispatch(createAction(LOGOUT_REQUEST, {}));
        return Core.authenticationManager.logout().then(
            () => {
                Core.cacheManager.removeTokenStore();
                dispatch(createAction(LOGOUT_SUCCESS, null));
            },
            () => dispatch(createAction(LOGOUT_ERROR, null)),
        );
    };
}

function loginError(dispatch, error, errorType) {
    dispatch(
        createAction(LOGIN_ERROR, {
            error: true,
            errorType,
        }),
    );
    throw error;
}

function loginErrorExpected(dispatch, error, errorType) {
    dispatch(
        createAction(LOGIN_ERROR, {
            error: true,
            errorType,
        }),
    );
}

function login2fa(dispatch, credentials) {
    let data2FA;
    Core.authenticationManager
        .login2fa(credentials)
        .then((res) => {
            const { token, language, ...data } = res;
            data2FA = data;
            Core.cacheManager.setTokenStore(token);
            dispatch(createAction(GET_LITERALS_REQUEST, {}));
            return Core.literalsManager.getLiterals(language, -1);
        })
        .then((res) => {
            dispatch(createAction(GET_LITERALS_SUCCESS, res));
            dispatch(
                createAction(OPEN_2FA, {
                    ...data2FA,
                    credentials: { ...credentials, userPass: credentials.password },
                    ready: true,
                }),
            );
        })
        .catch((error) => {
            loginError(dispatch, error, '2fa');
        });
}

function getFingerprint() {
    const parser = new UAParser();
    parser.setUA(navigator.userAgent);
    const ua = parser.getResult();
    const params = {
        platform: navigator.platform,
        deviceMemory: navigator.deviceMemory,
        cores: navigator.hardwareConcurrency,
        os: ua.os.name,
        browser: ua.browser.name,
    };

    return hash(params);
}

export function login(credentials) {
    return (dispatch) => {
        const { username, password } = credentials;

        if (!username || !password) {
            dispatch(
                createAction(LOGIN_ERROR, {
                    error: true,
                }),
            );
            return () => null;
        }

        dispatch(createAction(LOGIN_REQUEST, {}));

        credentials.fingerprint = getFingerprint();

        Core.authenticationManager
            .login(credentials)
            .then((res) => {
                if (res?.token) {
                    Core.cacheManager.setTokenStore(res.token);
                    dispatch(getLoggedUserData())
                        .then(dispatch(getPermissions()))
                        .then(dispatch(createAction(LOGIN_SUCCESS, null)))
                        .catch((error) => loginError(dispatch, error, 'general'));
                } else {
                    logout();
                }
            })
            .catch((error) => {
                if (
                    error.response?.status === 400 &&
                    error.response?.data?.error === '2FA Login is necessary'
                ) {
                    login2fa(dispatch, credentials);
                } else if (error.response?.status === 400 && error.response.data.code === 401) {
                    loginErrorExpected(dispatch, error, 'user_blocked');
                } else if (error.response?.status === 400) {
                    switch (error.response.data.error) {
                        case 'not allowed':
                            // Not permissions
                            loginErrorExpected(dispatch, error, 'permissions');
                            return;
                        default:
                            // Bad user password
                            loginErrorExpected(dispatch, error, 'login');
                    }
                } else {
                    loginError(dispatch, error, 'login');
                }
            });
    };
}

function _tokenByToken(token, isWebToken, credentials) {
    if (!isWebToken) return Promise.resolve({ token });
    return Core.authenticationManager.tokenByToken(credentials);
}

export function loginToken(token, isWebToken) {
    return (dispatch) => {
        const credentials = { token, fingerprint: getFingerprint() };

        dispatch(createAction(LOGIN_OAUTH_REQUEST, {}));

        Core.authenticationManager
            .logout()
            .then(() => Core.cacheManager.removeTokenStore())
            .then(() => _tokenByToken(token, isWebToken, credentials))
            .then((res) => {
                if (res?.token) {
                    Core.cacheManager.setTokenStore(res.token);
                    dispatch(getLoggedUserData())
                        .then(dispatch(getPermissions()))
                        .then(dispatch(createAction(LOGIN_OAUTH_SUCCESS, null)))
                        .catch((error) => loginError(dispatch, error, 'general'));
                } else {
                    loginError(dispatch, null, 'general');
                }
            })
            .catch((error) => {
                if (
                    error?.response?.status === 400 &&
                    error?.response?.data?.error === '2FA Login is necessary'
                ) {
                    dispatch(createAction(LOGIN_OAUTH_SUCCESS, null));
                    dispatch(createAction(LOGIN_REQUEST, {}));
                    login2fa(dispatch, credentials);
                } else {
                    loginError(dispatch, error, 'general');
                }
            });
    };
}

export function loginInfo(credentials) {
    return (dispatch) => {
        const { username } = credentials;
        if (!username) {
            dispatch(
                createAction(LOGIN_ERROR, {
                    error: true,
                }),
            );
            return () => null;
        }
        dispatch(createAction(LOGIN_INFO_REQUEST, {}));

        return Core.authenticationManager.loginInfo(credentials).then((userLoginInfo) => {
            dispatch(createAction(GET_LOGIN_INFO_SUCCESS, userLoginInfo.Result));
        });
    };
}

export function close2FA(error) {
    return (dispatch) => {
        if (error) {
            loginError(dispatch, error, '2fa');
        } else {
            dispatch(createAction(CLOSE_2FA, null));
        }
    };
}

export function finish2FA({ token }) {
    return (dispatch) => {
        Core.cacheManager.setTokenStore(token);
        dispatch(getLoggedUserData())
            .then(dispatch(getPermissions()))
            .then(dispatch(createAction(LOGIN_2FA_SUCCESS, null)))
            .catch((error) => loginError(dispatch, error, 'general'));
    };
}

export function switchImplementation(implId) {
    return (dispatch, getState) => {
        if (implId === undefined || implId.length === 0) {
            dispatch(
                createAction(SWITCH_IMPL_ERROR, {
                    error: true,
                }),
            );
            return Promise.reject(new Error('Invalid implementation id'));
        }

        const currentImpl = getState().authentication.impl;
        if (currentImpl != null && String(currentImpl) === String(implId)) {
            return Promise.resolve();
        }

        dispatch(createAction(SWITCH_IMPL_REQUEST, {}));
        return Core.authenticationManager.switchImplementation(implId).then(
            (token) => {
                if (token) {
                    Core.cacheManager.setTokenStore(token);
                    return dispatch(getPermissions())
                        .then(() => {
                            return Core.permissionsManager.changeScope(['pictures']);
                        })
                        .then((scopedToken) => {
                            dispatch(createAction(SET_SCOPED_TOKEN, { scopedToken }));
                        })
                        .then(() => {
                            dispatch(createAction(SWITCH_IMPL_SUCCESS, implId));
                            dispatch(createAction(CLEAR_ALL, {}));
                        });
                } else {
                    logout();
                }
            },
            (error) => {
                dispatch(
                    createAction(SWITCH_IMPL_ERROR, {
                        error: true,
                        errorType: error,
                    }),
                );
                throw error;
            },
        );
    };
}

export function getCfmAutoLogin() {
    return (dispatch) => {
        dispatch(createAction(GET_CFM_AUTOLOGIN_REQUEST, {}));
        return Core.authenticationManager.getCfmAutoLogin().then(
            (response) => dispatch(createAction(GET_CFM_AUTOLOGIN_SUCCESS, response)),
            (error) => {
                dispatch(
                    createAction(GET_CFM_AUTOLOGIN_ERROR, {
                        error: true,
                        errorType: error,
                    }),
                );
                throw error;
            },
        );
    };
}

export function externalAuthentication(authenticationServerAddress) {
    let returnPath = encodeURIComponent(window.location.origin + '/');
    let newPath = authenticationServerAddress
        .replace('https://change_me/', returnPath)
        .replace('https:%2f%2fchange_me%2f', returnPath);
    window.location.assign(newPath);
}

export function oauthCallbackLogin(state, redirectUrl) {
    return (dispatch) => {
        dispatch(createAction(LOGIN_OAUTH_REQUEST, {}));
        return Core.authenticationManager
            .oauthLogin(state, redirectUrl)
            .then((response) =>
                login({ username: response.Message, password: response.Result })(dispatch),
            );
    };
}

export function resetUserLoginInfo() {
    return (dispatch) => {
        dispatch(createAction(RESET_USER_LOGIN_INFO, {}));
    };
}
